import React, { useState, useRef } from 'react';
import './Profile.css'; // Make sure this CSS file is in the same directory

// --- SVG Icons ---
const IconUpload = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
);

const IconFileCheck = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <path d="M9 15l2 2 4-4"></path>
    </svg>
);

const IconTrash = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"></polyline>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    </svg>
);


// --- Dummy Employee Data ---
const dummyEmployeeData = {
  id: 'EMP789001',
  name: 'Ananya Sharma',
  email: 'ananya.sharma@examplecorp.com',
  joiningDate: '2025-11-15',
  designation: 'Software Engineer'
};


// --- Document Uploader Component ---
const DocumentUploader = ({ docType, title, file, onFileSelect, onFileRemove, allowedTypes }) => {
    const fileInputRef = useRef(null);
    const handleCardClick = () => fileInputRef.current.click();

    return (
        <div className={`document-card ${file ? 'uploaded' : 'pending'}`}>
            <div className="document-card-header">
                <div className="document-card-icon">
                    {file ? <IconFileCheck /> : <IconUpload />}
                </div>
                <div className="document-card-info">
                    <h4>{title}</h4>
                    <span className={`status-badge ${file ? 'status-uploaded' : 'status-pending'}`}>
                        {file ? 'Uploaded' : 'Pending Upload'}
                    </span>
                </div>
            </div>
            <div className="document-card-body">
                {file ? (
                    <div className="file-details">
                        <span className="file-name" title={file.name}>{file.name}</span>
                        <button
                            type="button"
                            className="remove-file-button"
                            title="Remove File"
                            onClick={() => onFileRemove(docType)}
                        >
                            <IconTrash />
                        </button>
                    </div>
                ) : (
                    <>
                        <p>Click to browse or drag and drop your file here.</p>
                        <button type="button" className="browse-button" onClick={handleCardClick}>
                            Browse Files
                        </button>
                    </>
                )}
            </div>
            <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => onFileSelect(docType, e.target.files[0])}
                style={{ display: 'none' }}
                accept={allowedTypes}
            />
        </div>
    );
};


// --- Main Onboarding Verification Component ---
const Profile = () => {
  const [documents, setDocuments] = useState({
    aadhar: null,
    pan: null,
    certificate: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleFileSelect = (docType, file) => {
    if (file) {
      setDocuments(prev => ({ ...prev, [docType]: file }));
    }
  };

  const handleFileRemove = (docType) => {
    setDocuments(prev => ({ ...prev, [docType]: null }));
  };
  
  const allDocumentsUploaded = documents.aadhar && documents.pan && documents.certificate;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!allDocumentsUploaded) {
        alert("Please upload all required documents before submitting.");
        return;
    }

    setIsSubmitting(true);
    setSuccessMessage('');

    // --- In a real application, you would send the files to a server here ---
    console.log("Submitting the following documents:", documents);
    
    // Simulate API call
    setTimeout(() => {
        setIsSubmitting(false);
        setSuccessMessage('Documents submitted successfully for verification!');
        // Here you might redirect the user or show a permanent success state.
    }, 2000);
  };


  return (
    <div className="onboarding-page-container">
      <div className="onboarding-panel">

        {/* --- Header Section --- */}
        <div className="onboarding-header">
          <h1>Onboarding Document Verification</h1>
          <p>Welcome, {dummyEmployeeData.name}! Please upload the required documents to complete your onboarding process.</p>
        </div>

        {/* --- Employee Details Section --- */}
        <div className="employee-details-section">
            <h3>Your Details</h3>
            <div className="details-grid">
                <div><strong>Employee ID:</strong> {dummyEmployeeData.id}</div>
                <div><strong>Full Name:</strong> {dummyEmployeeData.name}</div>
                <div><strong>Email:</strong> {dummyEmployeeData.email}</div>
                <div><strong>Joining Date:</strong> {dummyEmployeeData.joiningDate}</div>
            </div>
        </div>

        {/* --- Document Upload Section --- */}
        <div className="document-upload-section">
            <h3>Required Documents</h3>
            <form onSubmit={handleSubmit}>
                <div className="document-cards-container">
                    <DocumentUploader
                        docType="aadhar"
                        title="Aadhaar Card"
                        file={documents.aadhar}
                        onFileSelect={handleFileSelect}
                        onFileRemove={handleFileRemove}
                        allowedTypes=".pdf,.jpg,.jpeg,.png"
                    />
                    <DocumentUploader
                        docType="pan"
                        title="PAN Card"
                        file={documents.pan}
                        onFileSelect={handleFileSelect}
                        onFileRemove={handleFileRemove}
                        allowedTypes=".pdf,.jpg,.jpeg,.png"
                    />
                    <DocumentUploader
                        docType="certificate"
                        title="Educational Certificate"
                        file={documents.certificate}
                        onFileSelect={handleFileSelect}
                        onFileRemove={handleFileRemove}
                        allowedTypes=".pdf,.doc,.docx"
                    />
                </div>
                
                {successMessage && <div className="onboarding-message success-message">{successMessage}</div>}

                <div className="submission-area">
                    <button
                        type="submit"
                        className="submit-documents-button"
                        disabled={!allDocumentsUploaded || isSubmitting}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit All Documents'}
                    </button>
                    {!allDocumentsUploaded && (
                         <small className="form-note">Please upload all three documents to enable submission.</small>
                    )}
                </div>
            </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;